// components/shared/PrivacyPolicy.jsx
import React from "react";

const PrivacyPolicy = () => {
    return (
        <div className="min-h-screen bg-darkblue text-lightgray px-6 py-16">
            <div className="max-w-4xl mx-auto">
                <h1 className="text-4xl font-bold text-orange text-center mb-4">
                    Privacy Policy
                </h1>
                <p className="text-center italic mb-8">Last updated: 3/20/2025</p>

                <div className="space-y-6 text-lg">
                    {/* 1. Introduction */}
                    <section>
                        <h2 className="text-2xl font-semibold text-orange mb-2">1. Introduction</h2>
                        <p>
                            Welcome to Career Mission ("Company", "we", "us", or "our"). This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website, use our services, or otherwise interact with us at careermission.ai. Please read this policy carefully. By using our services, you agree to the collection and use of information in accordance with this Privacy Policy.
                        </p>
                    </section>

                    {/* 2. Information We Collect */}
                    <section>
                        <h2 className="text-2xl font-semibold text-orange mb-2">2. Information We Collect</h2>
                        <p>We may collect and process various types of information from you, including:</p>
                        <ul className="list-disc list-inside ml-4">
                            <li>
                                <strong>Personal Information:</strong> Such as your name, email address, phone number, and any other information you voluntarily provide when contacting us, signing up, or using our services.
                            </li>
                            <li>
                                <strong>Usage Data:</strong> Information automatically collected when you visit our website, including your IP address, browser type, operating system, pages visited, and other diagnostic data.
                            </li>
                            <li>
                                <strong>Cookies and Tracking Technologies:</strong> We may use cookies and similar technologies to enhance your experience on our site. For details, please see Section 5 below.
                            </li>
                        </ul>
                    </section>

                    {/* 3. How We Use Your Information */}
                    <section>
                        <h2 className="text-2xl font-semibold text-orange mb-2">3. How We Use Your Information</h2>
                        <ul className="list-disc list-inside ml-4">
                            <li>Provide, operate, and maintain our website and services.</li>
                            <li>Improve, personalize, and expand our website and services.</li>
                            <li>Understand and analyze how you use our website and services.</li>
                            <li>Communicate with you, including for customer support, updates, and marketing purposes (with your consent, where required by law).</li>
                            <li>Comply with legal obligations and enforce our agreements.</li>
                        </ul>
                    </section>

                    {/* 4. How We Share Your Information */}
                    <section>
                        <h2 className="text-2xl font-semibold text-orange mb-2">4. How We Share Your Information</h2>
                        <ul className="list-disc list-inside ml-4">
                            <li>
                                <strong>Service Providers:</strong> Trusted third parties who assist us in operating our website or providing our services, subject to confidentiality agreements.
                            </li>
                            <li>
                                <strong>Legal Compliance:</strong> When required by law, regulation, or legal process, or to protect the rights, property, or safety of the Company, our users, or others.
                            </li>
                            <li>
                                <strong>Business Transfers:</strong> In connection with any merger, sale, or reorganization of our business, your information may be transferred as part of that transaction.
                            </li>
                        </ul>
                    </section>

                    {/* 5. Cookies and Other Tracking Technologies */}
                    <section>
                        <h2 className="text-2xl font-semibold text-orange mb-2">5. Cookies and Other Tracking Technologies</h2>
                        <p>
                            We use cookies and similar tracking technologies to monitor activity on our website and hold certain information. Cookies help us improve your browsing experience by remembering your preferences and providing relevant content. You can adjust your browser settings to refuse cookies; however, please note that some parts of the website may not function properly as a result.
                        </p>
                    </section>

                    {/* 6. Security */}
                    <section>
                        <h2 className="text-2xl font-semibold text-orange mb-2">6. Security</h2>
                        <p>
                            We take reasonable measures to protect your information from unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the Internet or electronic storage is completely secure. While we strive to protect your information, we cannot guarantee its absolute security.
                        </p>
                    </section>

                    {/* 7. Data Retention */}
                    <section>
                        <h2 className="text-2xl font-semibold text-orange mb-2">7. Data Retention</h2>
                        <p>
                            We retain your personal information only for as long as necessary to fulfill the purposes for which it was collected, including for the purposes of satisfying any legal, accounting, or reporting requirements. When no longer needed, we will securely delete or anonymize your data.
                        </p>
                    </section>

                    {/* 8. Your Rights */}
                    <section>
                        <h2 className="text-2xl font-semibold text-orange mb-2">8. Your Rights</h2>
                        <p>
                            Depending on your jurisdiction, you may have certain rights regarding your personal data, such as:
                        </p>
                        <ul className="list-disc list-inside ml-4">
                            <li>The right to access and request a copy of your data.</li>
                            <li>The right to correct any inaccuracies in your data.</li>
                            <li>The right to request deletion of your data under certain circumstances.</li>
                            <li>The right to restrict or object to the processing of your data.</li>
                        </ul>
                        <p>
                            To exercise any of these rights, please contact us at the email address provided below.
                        </p>
                    </section>

                    {/* 9. Changes to This Privacy Policy */}
                    <section>
                        <h2 className="text-2xl font-semibold text-orange mb-2">9. Changes to This Privacy Policy</h2>
                        <p>
                            We may update our Privacy Policy from time to time. When we do, we will revise the "Last updated" date at the top of this page. We encourage you to review this Privacy Policy periodically to stay informed about how we are protecting your information.
                        </p>
                    </section>

                    {/* 10. Contact Us */}
                    <section>
                        <h2 className="text-2xl font-semibold text-orange mb-2">10. Contact Us</h2>
                        <p>
                            If you have any questions or concerns about this Privacy Policy or our data practices, please contact us at:
                        </p>
                        <p className="text-lg font-semibold">
                            <a href="mailto:contact@careermission.ai" className="text-lightblue hover:underline">
                                contact@careermission.ai
                            </a>
                        </p>
                    </section>
                </div>
            </div>
        </div>
    );
};

export default PrivacyPolicy;
